import React, { useState } from 'react';
import { Typography, Tag, Button, Input, Steps, Spin, Card } from 'antd';
import {
  FileTextOutlined, ThunderboltOutlined, ReloadOutlined,
  CheckCircleOutlined, BookOutlined, EditOutlined, EyeOutlined,
} from '@ant-design/icons';
import './index.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const DOMAINS = [
  { key:'highway', label:'高速公路收费', icon:'🛣️', desc:'ETC收费、清分结算、费率管理等', color:'#1677ff' },
  { key:'urban', label:'城市交通管控', icon:'🏙️', desc:'信号优化、拥堵治理、公交调度等', color:'#52c41a' },
  { key:'patrol', label:'智能巡检', icon:'🔍', desc:'道路病害检测、设施巡查、养护管理', color:'#fa8c16' },
  { key:'bridge', label:'桥梁健康监测', icon:'🌉', desc:'结构安全、位移应变、振动监测', color:'#722ed1' },
  { key:'simulation', label:'交通规划仿真', icon:'🖥️', desc:'路网仿真、方案评估、影响分析', color:'#13c2c2' },
  { key:'safety', label:'交通安全', icon:'🛡️', desc:'事故分析、风险预警、安全评估', color:'#eb2f96' },
];

const DEMOS: Record<string,{input:string;result:any}> = {
  highway:{ input:'我们管理的一段高速公路，最近半年隧道入口处追尾事故明显增多，尤其是夜间和雨天。想做一个智能预警系统，能根据实时天气、车速、车流量自动判断风险等级，并通过情报板提前预警驾驶员减速。同时希望能对接现有的ETC收费数据，分析哪些时段、哪些车型事故率偏高。',
    result:{ title:'高速公路隧道入口智能预警系统', overview:'基于实时交通数据、天气数据和历史事故数据的综合预警系统，通过情报板实现分级预警，降低隧道入口事故率。',
      modules:[
        { name:'数据采集与融合', points:['接入实时车速/流量检测器数据','接入气象站实时数据（能见度、降雨量）','对接ETC收费系统获取车型/时段通行数据','历史事故数据导入与标注'] },
        { name:'风险评估引擎', points:['多因子风险评估模型（车速+流量+天气+时段+历史）','三级预警阈值（黄色/橙色/红色）','动态阈值自适应调整'] },
        { name:'预警发布与联动', points:['情报板自动发布分级预警信息','预警信息同步推送至交警/路政平台','可变限速标志联动控制'] },
        { name:'数据分析与决策支持', points:['事故高发时段/车型/天气组合分析','预警效果量化评估','定期生成安全管理报告'] },
      ], acceptance:['实时接入检测器和气象站数据，延迟≤5秒','风险判定准确率≥85%','情报板预警下发延迟≤3秒','ETC数据对接完成，支持按车型/时段查询'] },
  },
  patrol:{ input:'我们负责南京市区主干道的日常养护巡检，目前全靠人工开车巡查拍照，效率低且容易漏检。想引入智能巡检系统，用公交车或养护车辆搭载摄像头和传感器，自动识别路面裂缝、坑槽、标线磨损等问题，定位精度做到具体车道和桩号，自动生成巡检报告和维修工单。',
    result:{ title:'城市道路智能巡检系统', overview:'基于车载视觉与传感器的道路病害自动识别与定位系统，替代人工巡检，实现养护管理智能化。',
      modules:[
        { name:'车载采集终端', points:['高清摄像头+激光雷达融合采集','GPS+惯性导航精确定位（车道级，误差≤0.5m）','支持公交/养护车辆搭载，即装即用'] },
        { name:'病害AI识别引擎', points:['路面裂缝自动检测与分级','坑槽面积与深度估算','标线磨损程度评估','井盖/护栏等设施异常检测'] },
        { name:'巡检管理与派单', points:['自动生成巡检报告（含照片、位置、病害等级）','智能派单：根据位置分配给最近养护班组','维修进度跟踪与验收'] },
      ], acceptance:['病害识别准确率≥90%，漏检率≤5%','定位精度车道级，误差≤0.5米','巡检报告自动生成，含GIS位置标注'] },
  },
};

export default function SmartReqPage() {
  const [domain, setDomain] = useState('highway');
  const [input, setInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [step, setStep] = useState(0);
  const active = DOMAINS.find(d=>d.key===domain)!;

  const handleGenerate = () => { if(!input.trim())return; setGenerating(true);setStep(1); setTimeout(()=>{ setResult((DEMOS[domain]||DEMOS.highway).result); setGenerating(false);setStep(2); },2000); };
  const handleLoadDemo = () => { const d=DEMOS[domain];if(d)setInput(d.input); };
  const handleReset = () => { setInput('');setResult(null);setStep(0); };

  return (
    <div className="smart-req-page" style={{ height:'100%', display:'flex', flexDirection:'column', background:'#f7f8fa' }}>
      {/* 标题栏 — 蓝紫渐变 */}
      <div className="smart-req-header" style={{ padding:'12px 20px', display:'flex',alignItems:'center',gap:8,
        background:'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', flexShrink:0 }}>
        <FileTextOutlined style={{fontSize:20,color:'#fff'}} />
        <Title level={5} style={{margin:0,color:'#fff'}}>智需中心</Title>
        <Text style={{color:'rgba(255,255,255,0.7)',fontSize:12,marginLeft:8}}>面向业主单位 — 选择知识库，自然语言描述需求，AI自动生成需求规格文档</Text>
      </div>

      {/* 步骤条 */}
      <div className="smart-req-steps" style={{ background:'#fff',borderRadius:0,padding:'12px 40px',borderBottom:'1px solid #f0f0f0' }}>
        <Steps current={step} size="small" items={[
          { title:<span style={{fontSize:12}}>选择领域知识库</span>, icon:<BookOutlined style={{color:step>=0?'#7c3aed':undefined}}/> },
          { title:<span style={{fontSize:12}}>自然语言描述需求</span>, icon:<EditOutlined style={{color:step>=1?'#7c3aed':undefined}}/> },
          { title:<span style={{fontSize:12}}>AI生成需求文档</span>, icon:<EyeOutlined style={{color:step>=2?'#7c3aed':undefined}}/> },
        ]} />
      </div>

      {/* 主区域 */}
      <div className="smart-req-body" style={{ flex:1, overflow:'auto', padding:'20px 24px', display:'flex',gap:20 }}>
        {/* 左侧 */}
        <div style={{ flex:'0 0 440px', display:'flex',flexDirection:'column',gap:16 }}>
          {/* 领域选择 */}
          <Card className="smart-req-card" size="small" title={<span style={{fontSize:13,fontWeight:600,color:'#0ea5e9'}}>📚 选择行业知识库领域</span>}
            style={{ borderRadius:10,border:'1px solid #f0f0f0' }}
            headStyle={{ borderBottom:'1px solid #f5f5f5',minHeight:40,background:'#faf5ff' }}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
              {DOMAINS.map(d => {
                const on = domain===d.key;
                return (
                  <div key={d.key} className={`smart-domain-card ${on ? 'active' : ''}`} onClick={()=>{setDomain(d.key);setInput('');setResult(null);setStep(0);}}
                    style={{ cursor:'pointer',padding:'12px 8px',borderRadius:8,textAlign:'center',
                      border:on?`2px solid ${d.color}`:'1px solid #f0f0f0',
                      background:on?`${d.color}10`:'#fff', transition:'all .2s' }}>
                    <div style={{fontSize:20,marginBottom:4}}>{d.icon}</div>
                    <div style={{fontSize:11,fontWeight:on?600:400,color:on?d.color:'#666'}}>{d.label}</div>
                  </div>
                );
              })}
            </div>
            <div style={{marginTop:10,fontSize:11,color:'#94a3b8',textAlign:'center',background:'#f8fafc',padding:'6px',borderRadius:6}}>
              当前：{active.label} — {active.desc}
            </div>
          </Card>

          {/* 需求输入 */}
          <Card className="smart-req-card" size="small" title={
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:13,fontWeight:600,color:'#7c3aed'}}>✍️ 描述业务需求</span>
              <Button type="link" size="small" onClick={handleLoadDemo} style={{fontSize:11,padding:0,height:'auto'}}>💡 加载示例</Button>
            </div>}
            style={{ borderRadius:10,flex:1,display:'flex',flexDirection:'column',border:'1px solid #f0f0f0' }}
            headStyle={{ borderBottom:'1px solid #f5f5f5',minHeight:40,background:'#faf5ff' }}
            bodyStyle={{ flex:1,display:'flex',flexDirection:'column' }}>
            <TextArea placeholder="请用自然语言描述业务问题或想要实现的功能..." value={input}
              onChange={e=>setInput(e.target.value)}
              style={{ flex:1,borderRadius:8,fontSize:13,border:'1px solid #f0f0f0',resize:'none',minHeight:140 }} />
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
              <Text type="secondary" style={{fontSize:11}}>{input.length} 字</Text>
              {step===2 && <Button onClick={handleReset} icon={<ReloadOutlined/>} size="small">重新输入</Button>}
            </div>
            <Button type="primary" size="large" block icon={<ThunderboltOutlined/>}
              onClick={handleGenerate} loading={generating} disabled={!input.trim()}
              style={{ marginTop:10,height:44,fontWeight:600,borderRadius:8,
                background:input.trim()?'linear-gradient(135deg, #7c3aed, #2563eb)':undefined, border:'none' }}>
              AI生成需求文档
            </Button>
          </Card>
        </div>

        {/* 右侧结果 */}
        <Card className="smart-req-card smart-req-result-card" size="small" title={<span style={{fontSize:13,fontWeight:600,color:'#0ea5e9'}}>📋 需求规格文档</span>}
          style={{ flex:1,borderRadius:10,border:'1px solid #f0f0f0' }}
          headStyle={{ borderBottom:'1px solid #f5f5f5',minHeight:40,background:'#faf5ff' }}
          bodyStyle={{ maxHeight:'calc(100vh - 260px)',overflow:'auto' }}>
          {step===0 && (
            <div style={{textAlign:'center',padding:'100px 0',color:'#d9d9d9'}}>
              <FileTextOutlined style={{fontSize:56,marginBottom:16}} />
              <div><Text type="secondary">在左侧选择领域并描述需求</Text></div>
              <div><Text type="secondary">AI将自动生成结构化需求文档</Text></div>
            </div>
          )}
          {step===1 && (
            <div style={{textAlign:'center',padding:'100px 0'}}>
              <Spin size="large" />
              <div style={{marginTop:16}}><Text style={{color:'#7c3aed',fontWeight:500}}>AI正在分析需求，生成文档...</Text></div>
            </div>
          )}
          {step===2 && result && (<>
            <Title level={5} style={{marginTop:0,marginBottom:8,color:active.color}}>{result.title}</Title>
            <div style={{fontSize:13,color:'#475569',lineHeight:1.8,marginBottom:20,padding:'12px 16px',
              background:`linear-gradient(135deg, ${active.color}08, ${active.color}12)`,borderRadius:8,
              border:`1px solid ${active.color}30`}}>
              <Text strong style={{fontSize:12,color:active.color}}>项目概述：</Text>{result.overview}
            </div>
            <Text strong style={{fontSize:13,display:'block',marginBottom:12,color:'#7c3aed'}}>功能模块</Text>
            {result.modules.map((m:any,i:number)=>(
              <div key={i} style={{marginBottom:14,paddingLeft:12,borderLeft:`3px solid ${active.color}`}}>
                <Text strong style={{fontSize:13,color:'#1e293b'}}>{i+1}. {m.name}</Text>
                <div style={{marginTop:4}}>
                  {m.points.map((p:string,j:number)=>(
                    <div key={j} style={{fontSize:12,color:'#475569',padding:'1px 0 1px 16px',lineHeight:1.8}}>• {p}</div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{marginTop:20,paddingTop:16,borderTop:'1px solid #f0f0f0'}}>
              <Text strong style={{fontSize:13,display:'block',marginBottom:8,color:'#22c55e'}}>✅ 验收标准</Text>
              {result.acceptance.map((a:string,i:number)=>(
                <div key={i} style={{fontSize:12,color:'#475569',padding:'2px 0 2px 20px',lineHeight:1.8}}>
                  <CheckCircleOutlined style={{color:'#22c55e',marginRight:6}}/>{a}
                </div>
              ))}
            </div>
            <div style={{marginTop:20,padding:'10px 14px',background:'#fffbeb',borderRadius:8,
              border:'1px solid #fde68a',fontSize:12,color:'#92400e',lineHeight:1.7}}>
              💡 <strong>提示：</strong>以上为AI基于{active.label}知识库生成的需求草案。技术方可直接基于此文档开发，需求方可实时参与确认。
            </div>
          </>)}
        </Card>
      </div>
    </div>
  );
}
